import { t } from '@lingui/core/macro'
import { Button } from 'designSystem/components/Button'
import type {
  CrossReferenceDocument,
  CrossReferenceState,
} from 'features/knowledgeDocumentViewer/utils/crossReference'
import { FileText, X } from 'lucide-react'
import styled from 'styled-components'

const Panel = styled.section`
  display: flex;
  flex-direction: column;
  flex: 0 1 auto;
  min-height: 8rem;
  max-height: max(10rem, calc(100% - 12rem));
  margin: 0.5rem;
  overflow: hidden;
  border: 0.0625rem solid var(--border);
  border-radius: 0.5rem;
  background: var(--muted);
  color: var(--foreground);
  font-size: 0.75rem;
  line-height: 1.4;
`
const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.375rem 0.5rem 0.125rem 0.625rem;
  flex-shrink: 0;
  strong {
    font-weight: 500;
  }
`
const Candidates = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.125rem;
  padding: 0 0.625rem 0.375rem;
  max-height: 7rem;
  overflow: auto;
  flex-shrink: 0;
  font-size: 0.6875rem;
  color: var(--muted-foreground);
`
const Candidate = styled.button`
  text-align: left;
  font: inherit;
  padding: 0.125rem 0.25rem;
  border: 0.0625rem solid transparent;
  border-radius: 0.25rem;
  background: transparent;
  color: inherit;
  cursor: pointer;
  overflow-wrap: anywhere;
  &[aria-pressed='true'] {
    background: var(--accent);
    color: var(--foreground);
    border-color: var(--border);
  }
  &:hover {
    background: var(--accent);
  }
  &:focus-visible {
    outline: 0.125rem solid var(--ring);
  }
`
const Content = styled.div`
  padding: 0 0.375rem 0.375rem;
  overflow: auto;
  min-height: 0;
  p {
    margin: 0.375rem 0.25rem;
    color: var(--muted-foreground);
  }
`
const Item = styled.button`
  width: 100%;
  display: flex;
  align-items: flex-start;
  gap: 0.375rem;
  text-align: left;
  padding: 0.375rem 0.25rem;
  border: 0;
  border-radius: 0.25rem;
  background: transparent;
  color: inherit;
  cursor: pointer;
  font: inherit;
  &:hover {
    background: var(--accent);
  }
  &:focus-visible {
    outline: 0.125rem solid var(--ring);
    outline-offset: -0.125rem;
  }
  & > svg {
    flex-shrink: 0;
    margin-top: 0.125rem;
    color: var(--muted-foreground);
  }
`
const ItemText = styled.span`
  display: flex;
  flex-direction: column;
  gap: 0.0625rem;
  min-width: 0;
  overflow-wrap: anywhere;
`
const Title = styled.span`
  font-size: 0.6875rem;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`
const Detail = styled.span`
  font-size: 0.625rem;
  line-height: 1.4;
  color: var(--muted-foreground);
`

function documentDetails(document: CrossReferenceDocument): string {
  const {
    drawing_number,
    title,
    document_type,
    sheet_number: sheet,
    revision_code: revision,
    page_number: page,
  } = document
  return [
    drawing_number && title,
    document_type.toLowerCase() !== 'other' && document_type,
    sheet && t`Sheet ${sheet}`,
    revision && t`Rev ${revision}`,
    page && t`Page ${page}`,
  ]
    .filter(Boolean)
    .join(' · ')
}

export function CrossReferencePanel({
  state,
  onClose,
  onRetry,
  onSelect,
  onSelectCandidate,
}: {
  state: CrossReferenceState
  onClose: () => void
  onRetry: () => void
  onSelectCandidate: (index: number) => void
  onSelect: (document: CrossReferenceDocument) => void
}) {
  return (
    <Panel aria-label={t`Cross-reference results`}>
      <Header>
        <strong>{t`Cross-reference`}</strong>
        <Button
          variant='ghost'
          size='icon-xs'
          aria-label={t`Close cross-reference`}
          onClick={onClose}>
          <X size={12} />
        </Button>
      </Header>
      <Content aria-live='polite'>
        {(state.candidates?.length ?? 0) > 1 && (
          <Candidates role='group' aria-label={t`Search for...`}>
            <span>{t`Search for...`}</span>
            {state.candidates?.map((candidate, index) => (
              <Candidate
                key={`${candidate.bbox.join(',')}:${candidate.text}`}
                aria-pressed={index === state.selectedCandidate}
                onClick={() => onSelectCandidate(index)}>
                {candidate.text}
              </Candidate>
            ))}
          </Candidates>
        )}
        {state.loading ? (
          <p>{t`Finding related documents…`}</p>
        ) : state.error ? (
          <>
            <p role='alert'>{state.error}</p>
            <Button variant='outline' onClick={onRetry}>
              {t`Try again`}
            </Button>
          </>
        ) : (
          <>
            {state.emptyRegion ? (
              <p>
                {t`No clear cross-reference target here. Try closer to a label or symbol.`}
              </p>
            ) : (
              state.documents?.length === 0 && (
                <p>{t`No related documents found for this region.`}</p>
              )
            )}
            {state.documents?.map((document) => (
              <Item
                key={
                  document.result_key ||
                  `${document.document_id}:${document.page_number}`
                }
                onClick={() => onSelect(document)}>
                <FileText size={13} aria-hidden />
                <ItemText>
                  <Title>
                    {document.drawing_number || document.title || t`Document`}
                  </Title>
                  <Detail>{documentDetails(document)}</Detail>
                </ItemText>
              </Item>
            ))}
          </>
        )}
      </Content>
    </Panel>
  )
}
